import { Link, useInRouterContext } from 'react-router-dom';
import { useTranslation, type TranslationKey } from '../i18n';
import { cx } from './ui/cx';

export const LEGAL_LINKS: ReadonlyArray<{
  to: string;
  labelKey: TranslationKey;
}> = [
  { to: '/legal/terms', labelKey: 'legal.link.terms' },
  { to: '/legal/privacy', labelKey: 'legal.link.privacy' },
  { to: '/legal/cookies', labelKey: 'legal.link.cookies' },
  { to: '/legal/age', labelKey: 'legal.link.age' },
];

export interface LegalLinksProps {
  className?: string;
  linkClassName?: string;
  align?: 'center' | 'start';
}

const DEFAULT_LINK =
  'inline-flex min-h-[44px] items-center px-2 text-sm text-white/70 underline-offset-4 hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hc-accent rounded-pill';

/**
 * The four legal pages as a labelled list of links. Works outside a router
 * too (the age gate renders in tests without one) by falling back to `<a>`.
 */
export default function LegalLinks({
  className,
  linkClassName,
  align = 'center',
}: LegalLinksProps) {
  const { t } = useTranslation();
  const inRouter = useInRouterContext();
  const linkClasses = cx(DEFAULT_LINK, linkClassName);

  return (
    <nav aria-label={t('legal.nav.label')} className={className}>
      <ul
        role="list"
        className={cx(
          'flex flex-wrap items-center gap-x-2 gap-y-0',
          align === 'center' ? 'justify-center' : '-ms-2 justify-start'
        )}
      >
        {LEGAL_LINKS.map(link => (
          <li key={link.to}>
            {inRouter ? (
              <Link to={link.to} className={linkClasses}>
                {t(link.labelKey)}
              </Link>
            ) : (
              <a href={link.to} className={linkClasses}>
                {t(link.labelKey)}
              </a>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}
